/**
 * Is a branch's pin usable?
 *
 * Mirrors the server's src/util/coordinateUtil.js rule for rule. A branch that fails here is
 * invisible to the multi-merchant nearest-branch ranking (GET /api/app/merchants) — it can
 * never be a "nearest location" for its brand, because we do not know where it is.
 *
 * Judged from the raw lat/lng strings rather than the derived latitude/longitude, so this
 * gives the same answer before and after the coordinate migration has run. When both are
 * present and disagree, that disagreement is itself reported — see `pendingBackfill`.
 */

// Must match CLEAN_DECIMAL in the server's coordinateUtil.js and the REGEXP in the
// coordinate backfill migration. See the `malformed` status below for why parseFloat alone
// is not enough.
const CLEAN_DECIMAL = /^[+-]?\d+(\.\d+)?$/;

const parse = (value, min, max) => {
  if (value === null || value === undefined || value === "") return null;
  const s = String(value).trim();
  if (!CLEAN_DECIMAL.test(s)) return null;
  const n = Number.parseFloat(s);
  if (!Number.isFinite(n)) return null;
  if (n < min || n > max) return null;
  return n;
};

export const COORD_STATUS = {
  ok: {
    label: "OK",
    tone: "green",
    hint: "This branch has a usable pin and can be ranked by distance.",
  },
  empty: {
    label: "NO PIN",
    tone: "coral",
    hint: "Latitude/longitude are blank. This branch will never appear as a nearest location.",
  },
  notANumber: {
    label: "BAD PIN",
    tone: "coral",
    hint: "Latitude/longitude are not numbers. Re-enter them as decimal degrees, e.g. 24.7136 / 46.6753.",
  },
  nullIsland: {
    label: "NO PIN",
    tone: "coral",
    hint: "Coordinates are 0, 0 — a placeholder, not a real location (that point is in the Gulf of Guinea). Set the branch's real coordinates.",
  },
  outOfRange: {
    label: "BAD PIN",
    tone: "coral",
    hint: "Out of range. Latitude must be between -90 and 90, longitude between -180 and 180. Values like 123 or 1111 are placeholders.",
  },
  malformed: {
    label: "BAD PIN",
    tone: "coral",
    hint: "Not a plain decimal number. Use a dot for the decimal point, with no spaces, commas or units — 24.7136, not 24,7136. A comma is read as 24, which would put this branch about 12 km from where it is.",
  },
  pendingBackfill: {
    label: "NOT INDEXED",
    tone: "orange",
    hint: "The coordinates look valid but the server has not indexed them yet. Run the branch-coordinates migration, or re-save this branch to refresh it.",
  },
};

/**
 * @param {object} branch a row from GET /api/branch/all
 * @returns {{ ok: boolean, issue: string, label: string, tone: string, hint: string }}
 */
export function coordinateStatus(branch) {
  const rawLat = branch?.lat;
  const rawLng = branch?.lng;

  let issue = "ok";

  const latStr = String(rawLat ?? "").trim();
  const lngStr = String(rawLng ?? "").trim();

  if (latStr === "" || lngStr === "") {
    issue = "empty";
  } else if (
    !Number.isFinite(Number.parseFloat(latStr)) ||
    !Number.isFinite(Number.parseFloat(lngStr))
  ) {
    // Nothing numeric in there at all.
    issue = "notANumber";
  } else if (!CLEAN_DECIMAL.test(latStr) || !CLEAN_DECIMAL.test(lngStr)) {
    // Parses to a number, but not the number that was written — "24,7136" reads as 24.
    // Separated from outOfRange because the fix is completely different: reformat, not replace.
    issue = "malformed";
  } else {
    const lat = parse(latStr, -90, 90);
    const lng = parse(lngStr, -180, 180);
    if (lat === null || lng === null) issue = "outOfRange";
    else if (lat === 0 && lng === 0) issue = "nullIsland";
  }

  // The pair parses, but the server has no derived value for it. Either the migration has
  // not been run or the row has not been written since it was — both mean this branch is
  // still missing from distance ranking despite looking correct on this screen. Surfacing it
  // separately turns "why isn't my branch showing up" into a one-glance answer.
  if (issue === "ok" && branch?.latitude === null && "latitude" in (branch || {})) {
    issue = "pendingBackfill";
  }

  // rawLat/rawLng ride along so the UI can show what is actually stored. "outOfRange" is far
  // more actionable when the row can also say the value is 1111.
  return {
    ok: issue === "ok",
    issue,
    rawLat: rawLat ?? "",
    rawLng: rawLng ?? "",
    ...COORD_STATUS[issue],
  };
}

/** Count of branches with an unusable pin. */
export const countMissingPins = (branches) =>
  (branches || []).filter((b) => !coordinateStatus(b).ok).length;
